import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, X } from "lucide-react";

const SEV_COLORS = { info: "#00E5FF", warning: "#F97316", danger: "#EF4444", success: "#10B981" };

export default function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);
  const nav = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.notifications); setUnread(data.unread_count);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, []);

  const markAll = async () => { await api.post("/notifications/read-all"); load(); };
  const openItem = async (n) => {
    await api.post(`/notifications/${n.id}/read`).catch(() => {});
    if (n.link) { nav(n.link); setOpen(false); }
    load();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative w-9 h-9 border border-[var(--border)] flex items-center justify-center hover:border-[var(--accent)]"
        data-testid="notif-bell"
      >
        <Bell className="w-4 h-4 text-[var(--text-dim)]" />
        {unread > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 bg-[var(--danger)] text-[var(--bg)] font-mono text-[10px] font-bold flex items-center justify-center rounded-full" data-testid="notif-count">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-96 bg-[var(--surface)] border border-[var(--border)] shadow-2xl z-40" data-testid="notif-panel">
          <div className="flex items-center justify-between p-3 border-b border-[var(--border)]">
            <div className="label-mono">NOTIFICACIONES · {unread} sin leer</div>
            {unread > 0 && (
              <button onClick={markAll} className="text-[10px] font-mono text-[var(--accent)] hover:underline" data-testid="notif-mark-all">
                Marcar todas
              </button>
            )}
          </div>
          <div className="max-h-[400px] overflow-auto">
            {items.length === 0 ? (
              <div className="p-6 text-center text-xs text-[var(--text-dim)] font-mono">Sin notificaciones</div>
            ) : items.map((n) => (
              <button
                key={n.id}
                onClick={() => openItem(n)}
                className={`w-full text-left p-3 border-b border-[var(--border)] block ${n.read ? "opacity-50" : ""} hover:bg-[var(--surface-2)]/60`}
                data-testid={`notif-${n.id}`}
              >
                <div className="flex items-start gap-2">
                  <div className="w-1.5 h-1.5 mt-1.5 rounded-full shrink-0" style={{ background: SEV_COLORS[n.severity] || "#94A3B8" }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-white truncate font-semibold">{n.title}</div>
                    <div className="text-xs text-[var(--text-dim)] leading-relaxed line-clamp-2">{n.body}</div>
                    <div className="label-mono mt-1">
                      {new Date(n.created_at).toLocaleString("es-ES", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" })}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
